import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'

import { Modal } from 'ggx-componentlibrary/components/modal/modal.component'
import { Button } from 'ggx-componentlibrary/components/button/button.component'
import {
  SmallText,
  TextLink,
  BodyBold,
} from 'ggx-componentlibrary/design/typography/typography.styles'
import { formatCurrency } from 'ggx-service/i18n/format-currency.service'

import {
  StyledRadioButton,
  OptionContainer,
} from './payment-method-modal.component.styles'
import { CreditCardGroup } from 'ggx-src/billing/components/credit-card-group.component'
import { ConnectedTopupModal } from 'ggx-src/billing/topup-modal/topup-modal.component'
import { getTotalCredit } from 'ggx-src/billing/duck/selectors'
import { getFeatureFlag } from 'ggx-service/feature-flag/selectors'

import { getAddCreditCardModalInfo } from 'ggx-global/duck/selectors'
import {
  showAddCreditCardModal,
  hideAddCreditCardModal,
} from 'ggx-global/duck/actions'
import { AddCreditCardModal } from 'ggx-src/billing/components/add-credit-card-modal.component'
import { EditCreditCardModal } from 'ggx-src/billing/components/edit-credit-card-modal.component'

import { CreditCardSection } from './credit-card-section'
import { getCurrentUserLevel } from 'ggx-src/users-and-branches/duck/selectors'

const PaymentMethod = ({
  t,
  paymentMethod,
  handlePaymentSelection,
  selectedPaymentMethod,
  totalCredit,
  sufficientBalance,
  showTopupModal,
  handleTopupModalClose,
  handleTopupModalOpen,
}) => {
  const userLevel = useSelector(state => getCurrentUserLevel(state))
  const canModifyCreditCards =
    userLevel !== 'b2b_branch_user' && userLevel !== 'b2b_sub_user'
  switch (paymentMethod) {
    case 'cash': {
      return (
        <OptionContainer>
          <StyledRadioButton
            onChange={handlePaymentSelection}
            option={{ label: t('payment.paymentMethods.cash'), value: 'cash' }}
            checked={selectedPaymentMethod === 'cash'}
            dataTestid="pay-by-cash"
          />
        </OptionContainer>
      )
    }
    case 'credit_card': {
      return (
        <OptionContainer>
          <StyledRadioButton
            onChange={handlePaymentSelection}
            option={{ label: t('common__credit_card'), value: 'credit_card' }}
            checked={selectedPaymentMethod === 'credit_card'}
            dataTestid="pay-by-credit-card"
          />
          <CreditCardGroup />
        </OptionContainer>
      )
    }
    case 'prepaid_wallet': {
      return (
        <>
          <OptionContainer>
            <StyledRadioButton
              onChange={handlePaymentSelection}
              option={{
                label: `${t('common__wallet')} (${formatCurrency({
                  amount: totalCredit,
                })})`,
                value: 'prepaid_wallet',
              }}
              checked={selectedPaymentMethod === 'prepaid_wallet'}
              dataTestid="pay-by-wallet"
            />
            {canModifyCreditCards &&
              (sufficientBalance ? (
                <SmallText>
                  <TextLink onClick={handleTopupModalOpen}>
                    {t(
                      'newOrder.tab.summary.paymentMethod.balance.sufficient.topup'
                    )}
                  </TextLink>
                  {t(
                    'newOrder.tab.summary.paymentMethod.balance.sufficient.label'
                  )}
                </SmallText>
              ) : (
                <SmallText typographyStyles="mb-xxs">
                  {t(
                    'newOrder.tab.summary.paymentMethod.balance.insufficient.label'
                  )}
                  <TextLink onClick={handleTopupModalOpen}>
                    {t(
                      'newOrder.tab.summary.paymentMethod.balance.insufficient.topup'
                    )}
                  </TextLink>
                </SmallText>
              ))}
            {showTopupModal && (
              <ConnectedTopupModal handleModalClose={handleTopupModalClose} />
            )}
          </OptionContainer>

          <SmallText typographyStyles="mb-none">
            {t('payment__you_wont_be_charged')}
          </SmallText>
        </>
      )
    }
    default:
      break
  }
}

const PaymentMethodModal = ({
  handleModalClose,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  sufficientBalance,
  availablePaymentMethods,
}) => {
  const [t] = useTranslation()
  const dispatch = useDispatch()

  const totalCredit = useSelector(state => getTotalCredit(state))
  const [showTopupModal, setShowTopupModal] = useState(false)

  const [temporarySelection, setTemporarySelection] = useState(selectedPaymentMethod)
  const handlePaymentSelection = event => {
    setTemporarySelection(event.target.value)
  }

  const handleTopupModalClose = useCallback(() => setShowTopupModal(false), [])
  const handleTopupModalOpen = useCallback(() => setShowTopupModal(true), [])

  const isCreditCardAvailable = availablePaymentMethods.includes('credit_card')
  const otherPaymentMethods = availablePaymentMethods
    .filter(method => method !== 'credit_card')
    .sort((a, b) => a.localeCompare(b))

  const addCreditCardModalInfo = useSelector(state =>
    getAddCreditCardModalInfo(state)
  )
  const [displayEditCreditCardInfo, setDisplayEditCreditCardInfo] = useState({})

  return (
    <>
      {!(
        addCreditCardModalInfo?.visible || !isEmpty(displayEditCreditCardInfo)
      ) && (
        <Modal
          height="initial"
          header={t('common__pay_with')}
          footer={
            <Button buttonType="primary" onClick={() => {
              setSelectedPaymentMethod(temporarySelection)
              handleModalClose()
            }}>
              {t('button__ok')}
            </Button>
          }
          onRequestClose={handleModalClose}
        >
          {
            <>
              {isCreditCardAvailable && (
                <CreditCardSection
                  valuePrefix="credit_card_"
                  selectedPaymentMethod={temporarySelection}
                  handlePaymentSelection={handlePaymentSelection}
                  onClickAdd={() => dispatch(showAddCreditCardModal())}
                  onClickEdit={card => setDisplayEditCreditCardInfo(card)}
                />
              )}
              {otherPaymentMethods.length && (
                <>
                  <BodyBold typographyStyles="mb-s">
                    {t('other_payment_methods')}
                  </BodyBold>
                  {otherPaymentMethods.map(paymentMethod => {
                    return (
                      <PaymentMethod
                        key={paymentMethod}
                        t={t}
                        paymentMethod={paymentMethod}
                        handlePaymentSelection={handlePaymentSelection}
                        selectedPaymentMethod={temporarySelection}
                        totalCredit={totalCredit}
                        sufficientBalance={sufficientBalance}
                        showTopupModal={showTopupModal}
                        handleTopupModalClose={handleTopupModalClose}
                        handleTopupModalOpen={handleTopupModalOpen}
                      />
                    )
                  })}
                </>
              )}
            </>
          }
        </Modal>
      )}
      {addCreditCardModalInfo?.visible && (
        <AddCreditCardModal
          onModalClose={() => dispatch(hideAddCreditCardModal())}
          cardItent={addCreditCardModalInfo.cardItent}
        />
      )}
      {!isEmpty(displayEditCreditCardInfo) && (
        <EditCreditCardModal
          {...displayEditCreditCardInfo}
          onModalClose={() => setDisplayEditCreditCardInfo({})}
        />
      )}
    </>
  )
}

PaymentMethodModal.propTypes = {
  handleModalClose: PropTypes.func,
  selectedPaymentMethod: PropTypes.string,
  setSelectedPaymentMethod: PropTypes.func,
  sufficientBalance: PropTypes.bool,
}

export { PaymentMethodModal }
